import React from 'react';
import './GlanceVisuals.css';

export type GlanceSlice = {
  key: string;
  value: number;
  label: string;
  color: string;
  lensKey?: string;
};

type PickHandler = (lensKey: string) => void;

const donutSegments = (slices: GlanceSlice[], radius: number) => {
  const circumference = 2 * Math.PI * radius;
  const total = slices.reduce((sum, slice) => sum + slice.value, 0) || 1;
  const drawn = slices.filter((slice) => slice.value > 0);
  const gap = drawn.length > 1 ? 4 : 0;
  let cursor = 0;
  return drawn.map((slice) => {
    const raw = (slice.value / total) * circumference;
    const length = Math.max(1, raw - gap);
    const segment = { key: slice.key, color: slice.color, length, offset: cursor, circumference };
    cursor += raw;
    return segment;
  });
};

export const GlanceDonut: React.FC<{
  percent: number;
  caption: string;
  ariaLabel: string;
  slices: GlanceSlice[];
}> = ({ percent, caption, ariaLabel, slices }) => {
  const radius = 42;
  const segments = donutSegments(slices, radius);
  return (
    <div className="glance-fill glance-donut">
      <svg className="dashboard-glance-gauge glance-donut-svg" viewBox="0 0 120 120" role="img" aria-label={ariaLabel}>
        <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" />
        {segments.map((segment) => (
          <circle
            key={segment.key}
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth="14"
            strokeLinecap="butt"
            strokeDasharray={`${segment.length} ${segment.circumference - segment.length}`}
            strokeDashoffset={-segment.offset}
            transform="rotate(-90 60 60)"
          />
        ))}
        <text x="60" y="56" textAnchor="middle" className="glance-donut-pct">{percent}</text>
        <text x="60" y="74" textAnchor="middle" className="glance-donut-unit">%</text>
      </svg>
      <div className="glance-mix" aria-hidden="true">
        {slices.filter((slice) => slice.value > 0).map((slice) => (
          <i key={slice.key} style={{ flexGrow: slice.value, background: slice.color }} title={`${slice.label} ${slice.value}`} />
        ))}
      </div>
      <p>{caption}</p>
    </div>
  );
};

export const GlanceBars: React.FC<{
  slices: GlanceSlice[];
  selectedLens?: string;
  onPick?: PickHandler;
}> = ({ slices, selectedLens, onPick }) => {
  const max = Math.max(1, ...slices.map((slice) => slice.value));
  return (
    <ul className="glance-bars">
      {slices.map((slice) => {
        const selected = Boolean(slice.lensKey && selectedLens === slice.lensKey);
        const width = `${Math.max(8, Math.round((slice.value / max) * 100))}%`;
        const body = (
          <>
            <span className="glance-bars-label">{slice.label}</span>
            <span className="glance-bars-track">
              <i style={{ width, background: slice.color }} />
            </span>
            <strong>{slice.value}</strong>
          </>
        );
        return (
          <li key={slice.key}>
            {onPick && slice.lensKey ? (
              <button
                type="button"
                className={selected ? 'is-selected' : undefined}
                aria-pressed={selected}
                onClick={() => onPick(slice.lensKey || '')}
              >
                {body}
              </button>
            ) : (
              <div>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/** Proportional pipeline: one stacked bar + compact chips (no empty tall cards). */
export const GlanceFlow: React.FC<{
  steps: GlanceSlice[];
  selectedLens?: string;
  onPick?: PickHandler;
}> = ({ steps, selectedLens, onPick }) => {
  const total = steps.reduce((sum, step) => sum + step.value, 0);
  return (
    <div className="glance-flow">
      <div
        className="glance-flow-stack"
        role="img"
        aria-label={steps.map((step) => `${step.label} ${step.value}`).join(', ')}
      >
        {steps.map((step) => {
          const share = total > 0 ? (step.value / total) * 100 : 0;
          if (share <= 0) return null;
          return (
            <i
              key={step.key}
              style={{ flexGrow: Math.max(share, 2), background: step.color }}
              title={`${step.label} ${step.value}`}
            />
          );
        })}
        {total <= 0 ? <i className="glance-flow-stack-empty" /> : null}
      </div>
      <ol className="glance-flow-chips">
        {steps.map((step) => {
          const selected = Boolean(step.lensKey && selectedLens === step.lensKey);
          const body = (
            <>
              <i style={{ background: step.color }} aria-hidden="true" />
              <strong>{step.value}</strong>
              <span>{step.label}</span>
            </>
          );
          return (
            <li key={step.key}>
              {onPick && step.lensKey ? (
                <button
                  type="button"
                  className={selected ? 'is-selected' : undefined}
                  aria-pressed={selected}
                  onClick={() => onPick(step.lensKey || '')}
                >
                  {body}
                </button>
              ) : (
                <div>{body}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const GlanceColumns: React.FC<{
  slices: GlanceSlice[];
  selectedLens?: string;
  onPick?: PickHandler;
}> = ({ slices, selectedLens, onPick }) => {
  const max = Math.max(1, ...slices.map((slice) => slice.value));
  return (
    <ul className="glance-columns">
      {slices.map((slice) => {
        const selected = Boolean(slice.lensKey && selectedLens === slice.lensKey);
        const height = `${Math.max(12, Math.round((slice.value / max) * 100))}%`;
        const body = (
          <>
            <span className="glance-column-plot" aria-hidden="true">
              <i style={{ height, background: slice.color }} />
            </span>
            <strong>{slice.value}</strong>
            <span>{slice.label}</span>
          </>
        );
        return (
          <li key={slice.key}>
            {onPick && slice.lensKey ? (
              <button
                type="button"
                className={selected ? 'is-selected' : undefined}
                aria-pressed={selected}
                onClick={() => onPick(slice.lensKey || '')}
              >
                {body}
              </button>
            ) : (
              <div>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export type GlanceDateRow = {
  key: string;
  date: string;
  title: string;
  place: string;
};

export const GlanceTimeline: React.FC<{
  rows: GlanceDateRow[];
  emptyLabel: string;
  onOpen?: (key: string) => void;
}> = ({ rows, emptyLabel, onOpen }) => {
  if (!rows.length) {
    return <p className="glance-timeline-empty">{emptyLabel}</p>;
  }
  return (
    <ol className="glance-timeline">
      {rows.map((row) => (
        <li key={row.key}>
          <time dateTime={row.date}>{row.date}</time>
          {onOpen ? (
            <button type="button" onClick={() => onOpen(row.key)}>{row.title}</button>
          ) : (
            <span>{row.title}</span>
          )}
          {row.place ? <em>{row.place}</em> : null}
        </li>
      ))}
    </ol>
  );
};
